import React, { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiBookOpen,
  FiChevronDown,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiSettings,
  FiUser,
  FiUserPlus,
  FiUsers,
  FiX,
} from "react-icons/fi";
import EmeraldSwitch from "../../../../components/EmeraldSwitch";
import http from "../../../../api/apiClient";

const escapeVCardValue = (value) =>
  typeof value === "string"
    ? value.replace(/\r?\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/:/g, "\\:")
    : "";

const normalizePhone = (value) =>
  typeof value === "string" ? value.replace(/\s+/g, "") : "";

const splitDisplayName = (displayName) => {
  if (!displayName) return { firstName: "", lastName: "" };
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const lastName = parts.pop();
  return { firstName: parts.join(" "), lastName };
};

const buildVCardContent = (card, opts = {}) => {
  const { contextChildName } = opts;

  const formattedName =
    (card.displayName && card.displayName.trim()) ||
    [card.firstName, card.lastName].filter(Boolean).join(" ").trim();
  const { firstName, lastName } = splitDisplayName(formattedName);
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];
  const safeFN = escapeVCardValue(formattedName || card.email || "Contact");
  lines.push(`FN:${safeFN}`);
  lines.push(`N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`);

  if (card.email) {
    lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardValue(card.email)}`);
  }
  if (card.phoneMobile) {
    lines.push(`TEL;TYPE=CELL,VOICE:${escapeVCardValue(normalizePhone(card.phoneMobile))}`);
  }

  if (card.addressStreet || card.addressPostalCode || card.addressCity) {
    const street = escapeVCardValue(card.addressStreet || "");
    const city = escapeVCardValue(card.addressCity || "");
    const postal = escapeVCardValue(card.addressPostalCode || "");
    const adr = ["", "", street, city, "", postal, ""].join(";");
    lines.push(`ADR;TYPE=HOME:${adr}`);
  }

  if (contextChildName) {
    const child = escapeVCardValue(contextChildName);
    lines.push(`NOTE:${escapeVCardValue(`Linked child - ${child}`)}`);
    lines.push(`item1.X-ABRELATEDNAMES:${child}`);
    lines.push(`item1.X-ABLabel:_$!<Child>!$_`);
  }

  if (Array.isArray(card.matchedChildren) && card.matchedChildren.length > 0) {
    const childrenNames = card.matchedChildren
      .map((kid) => (typeof kid.name === "string" ? kid.name : ""))
      .filter(Boolean)
      .join(", ");
    if (childrenNames) {
      lines.push(`NOTE:${escapeVCardValue(`Children - ${childrenNames}`)}`);
    }
  }

  lines.push("END:VCARD");
  return lines.join("\r\n");
};

const buildFileName = (displayName) => {
  const base = (displayName || "contact").trim().toLowerCase();
  const safe = base.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
  return `${safe || "contact"}.vcf`;
};

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const getAddressText = (card) => {
  if (card?.address) return card.address;
  return [card?.addressStreet, [card?.addressPostalCode, card?.addressCity].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
};

const FieldIcon = ({ children }) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200">
    {children}
  </span>
);

const DirectoryTab = ({
  t,
  canViewDirectory,
  openDirSettings,
  dirSettingsOpen,
  setDirSettingsOpen,
  saveDirectorySettings,
  turnOffListing,
  myDirErr,
  myDirSaving,
  dirQ,
  setDirQ,
  dirLoading,
  setDirData,
  dirInputRef,
  setDirPage,
  share,
  setShare,
  previewValues,
}) => {
  const [expandedContactKey, setExpandedContactKey] = useState(null);

  const [unifiedData, setUnifiedData] = useState(null);
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [unifiedError, setUnifiedError] = useState(null);

  const directoryTitle = t("parentPortal.directory.title", { defaultValue: "Directory" });
  const genericSearchPlaceholder = t("parentPortal.directory.search_placeholder", {
    defaultValue: "Search parents, children or teachers...",
  });

  const normalizeParentContacts = (item) => {
    const rawParents =
      (Array.isArray(item?.parents) && item.parents) ||
      (Array.isArray(item?.parentContacts) && item.parentContacts) ||
      (Array.isArray(item?.relatedParents) && item.relatedParents) ||
      [];

    if (rawParents.length > 0) {
      return rawParents
        .map((parent) => {
          const displayName =
            parent?.displayName ||
            parent?.name ||
            [parent?.firstName, parent?.lastName].filter(Boolean).join(" ").trim() ||
            "";
          const email = parent?.email || "";
          const phoneMobile = parent?.phone || parent?.phoneMobile || "";
          const address = parent?.address || getAddressText(parent);
          const availableActions = Array.isArray(parent?.availableActions)
            ? parent.availableActions.map((action) => String(action).toUpperCase())
            : null;

          return {
            ...parent,
            type: "PARENT",
            id: parent?.id || parent?.parentId,
            parentId: parent?.parentId || parent?.id,
            displayName,
            initials: parent?.initials || getInitials(displayName),
            email,
            phoneMobile,
            address,
            availableActions,
          };
        })
        .filter((parent) => parent.displayName);
    }

    if (Array.isArray(item?.parentNames)) {
      return item.parentNames
        .filter(Boolean)
        .map((name, index) => ({
          type: "PARENT",
          id: `parent-name-${index}`,
          parentId: `parent-name-${index}`,
          displayName: name,
          initials: getInitials(name),
          email: "",
          phoneMobile: "",
          address: "",
          availableActions: [],
          isNameOnly: true,
        }));
    }

    return [];
  };

  const normalizeDirectoryResult = (item) => {
    const type = String(item?.type || item?.resultType || "PARENT").toUpperCase();
    const displayName =
      item?.displayName ||
      item?.name ||
      [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim() ||
      t("common.unknown", { defaultValue: "Unknown" });
    const address = item?.address || getAddressText(item);
    const availableActions = Array.isArray(item?.availableActions)
      ? item.availableActions.map((action) => String(action).toUpperCase())
      : null;

    return {
      ...item,
      type,
      displayName,
      initials: item?.initials || getInitials(displayName),
      parentId: item?.parentId || item?.id,
      childId: item?.childId || item?.id,
      email: item?.email || "",
      phoneMobile: item?.phone || item?.phoneMobile || "",
      address,
      classroomName: item?.classroomName || item?.classroom || "",
      parents: normalizeParentContacts(item),
      parentNames: Array.isArray(item?.parentNames) ? item.parentNames.filter(Boolean) : [],
      availableActions,
    };
  };

  const extractResults = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
  };

  const content = extractResults(unifiedData).map(normalizeDirectoryResult);
  const hasResults = content.length > 0;
  const totalElements = Number.isFinite(unifiedData?.totalElements)
    ? unifiedData.totalElements
    : Number.isFinite(unifiedData?.total)
      ? unifiedData.total
      : content.length;
  const dirIsLoading = dirLoading || unifiedLoading;

  useEffect(() => {
    if (!canViewDirectory) return undefined;

    setExpandedContactKey(null);
    setUnifiedError(null);

    const q = dirQ.trim();
    if (!q) {
      setUnifiedData(null);
      setDirData?.(null);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setDirPage?.(0);
      setUnifiedLoading(true);
      try {
        const response = await http.get("/parent-portal/directory/search", {
          params: { q, limit: 25 },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          const payload = response?.data ?? [];
          setUnifiedData(payload);
          setDirData?.(payload);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setUnifiedData({ content: [] });
          setUnifiedError(
            error?.response?.data?.message ||
              t("parentPortal.directory.search_failed", { defaultValue: "Searching the directory failed." })
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setUnifiedLoading(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [canViewDirectory, dirQ, setDirData, setDirPage, t]);

  const submitDirectorySearch = async () => {
    const q = dirQ.trim();
    if (!q) {
      setUnifiedData(null);
      setDirData?.(null);
      return;
    }

    setExpandedContactKey(null);
    setUnifiedError(null);
    setDirPage?.(0);
    setUnifiedLoading(true);
    try {
      const response = await http.get("/parent-portal/directory/search", {
        params: { q, limit: 25 },
      });
      const payload = response?.data ?? [];
      setUnifiedData(payload);
      setDirData?.(payload);
    } catch (error) {
      setUnifiedData({ content: [] });
      setUnifiedError(
        error?.response?.data?.message ||
          t("parentPortal.directory.search_failed", { defaultValue: "Searching the directory failed." })
      );
    } finally {
      setUnifiedLoading(false);
    }
  };

  const openMap = (raw) => {
    if (!raw || typeof window === "undefined") return;
    const query = encodeURIComponent(raw);
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);

    if (isIOS) {
      window.location.href = `http://maps.apple.com/?q=${query}`;
      return;
    }

    if (isAndroid) {
      window.location.href = `geo:0,0?q=${query}`;
      return;
    }

    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank", "noopener,noreferrer");
  };

  const downloadVCard = async (card, opts = {}) => {
    if (typeof window === "undefined") return;

    const contentValue = buildVCardContent(card, opts);
    const fileName = buildFileName(card.displayName);
    const blob = new Blob([contentValue], { type: "text/vcard;charset=utf-8" });

    const ua = navigator.userAgent || "";
    // iPadOS 13+ reports itself as "Macintosh"; every iOS browser is WebKit-based.
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);

    const file = new File([blob], fileName, { type: "text/vcard" });
    if (!isIOS && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Contact", text: "Add to contacts" });
        return;
      } catch {
        // user cancel / unsupported fallback
      }
    }

    if (isIOS) {
      // iOS Safari blocks top-level navigation to data: URLs, so the old data: URL did nothing.
      // Opening a blob URL in the same tab makes iOS show its "Create New Contact" sheet.
      const url = URL.createObjectURL(blob);
      window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderSettings = () => {
    const fields = [
      ["displayName", t("parentPortal.name", { defaultValue: "Name" }), FiUser],
      ["email", t("parentPortal.email", { defaultValue: "Email" }), FiMail],
      ["phoneMobile", t("parentPortal.phone_mobile", { defaultValue: "Mobile" }), FiPhone],
      ["addressStreet", t("parentPortal.address", { defaultValue: "Address" }), FiMapPin],
      ["addressPostalCode", t("parentPortal.zipcode", { defaultValue: "Postal code" }), FiMapPin],
      ["addressCity", t("parentPortal.city", { defaultValue: "City" }), FiMapPin],
    ];

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDirSettingsOpen(false)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-800 shadow-sm ring-1 ring-slate-100 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700"
            aria-label={t("common.back", { defaultValue: "Back" })}
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h3 className="m-0 text-xl font-black tracking-tight text-slate-950 dark:text-white">
              {t("parentPortal.directory.share_title", { defaultValue: "Directory settings" })}
            </h3>
            <p className="m-0 mt-1 text-sm font-medium leading-6 text-slate-500 dark:text-slate-300">
              {t("parentPortal.directory.share_intro", {
                defaultValue: "Choose which details you want to share with other parents.",
              })}
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-[1.75rem] bg-white shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/80 dark:ring-slate-700/80">
          {fields.map(([key, label, Icon], index) => (
            <div
              key={key}
              className={`flex items-center gap-3 px-4 py-4 ${
                index > 0 ? "border-t border-slate-100 dark:border-slate-800" : ""
              }`}
            >
              <FieldIcon>
                <Icon className="h-4 w-4" />
              </FieldIcon>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-slate-950 dark:text-slate-50">{label}</div>
                <div className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                  {previewValues?.[key] || t("common.none", { defaultValue: "Not filled in" })}
                </div>
              </div>
              <EmeraldSwitch
                checked={!!share?.[key]}
                onChange={(val) => setShare((current) => ({ ...current, [key]: val }))}
                disabled={myDirSaving}
              />
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <button
            type="button"
            className="w-full rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-extrabold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700 disabled:opacity-60 dark:bg-emerald-500 dark:hover:bg-emerald-400"
            onClick={saveDirectorySettings}
            disabled={myDirSaving}
          >
            {myDirSaving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}
          </button>

          {canViewDirectory && (
            <button
              type="button"
              onClick={turnOffListing}
              disabled={myDirSaving}
              className="w-full rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-500 shadow-sm ring-1 ring-slate-100 transition hover:text-red-600 disabled:opacity-60 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700 dark:hover:text-red-300"
            >
              {t("parentPortal.directory.turn_off", { defaultValue: "Turn off listing" })}
            </button>
          )}
        </div>

        {myDirErr && (
          <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 ring-1 ring-red-100 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-800">
            {myDirErr}
          </p>
        )}
      </div>
    );
  };

  const buildContactKey = (card, index) => `${card.type || "RESULT"}-${card.parentId || card.childId || card.id || card.displayName || "contact"}-${index}`;

  const renderContactCard = (card, index) => {
    const displayName = card.displayName || t("common.unknown", { defaultValue: "Unknown" });
    const addressText = getAddressText(card);
    const contactKey = buildContactKey(card, index);
    const isExpanded = expandedContactKey === contactKey;
    const isChild = card.type === "CHILD";
    const isTeacher = card.type === "TEACHER";
    const hasEmail = !!card.email;
    const hasPhone = !!card.phoneMobile;
    const hasAddress = !!addressText;
    const hasClassroom = !!card.classroomName;
    const parentContacts = Array.isArray(card.parents) ? card.parents : [];
    const hasParentContacts = parentContacts.length > 0;
    const hasParents = hasParentContacts || (Array.isArray(card.parentNames) && card.parentNames.length > 0);
    const hasDetails = hasEmail || hasPhone || hasAddress || hasClassroom || hasParents;
    const availableActions = Array.isArray(card.availableActions) ? card.availableActions : null;
    const actionAllowed = (action, fallback) => (availableActions ? availableActions.includes(action) : fallback);
    const canAdd = actionAllowed("ADD", !isChild && (hasEmail || hasPhone || hasAddress || !!displayName));

    const actionButtons = [
      actionAllowed("MAIL", hasEmail)
        ? {
            key: "mail",
            as: "a",
            href: `mailto:${card.email}`,
            icon: FiMail,
            label: t("parentPortal.directory.email_parent", { defaultValue: "Mail" }),
          }
        : null,
      actionAllowed("CALL", hasPhone)
        ? {
            key: "call",
            as: "a",
            href: `tel:${card.phoneMobile.replace(/\s+/g, "")}`,
            icon: FiPhone,
            label: t("parentPortal.directory.call_parent", { defaultValue: "Call" }),
          }
        : null,
      actionAllowed("MAP", hasAddress)
        ? {
            key: "map",
            as: "button",
            onClick: (event) => {
              event.stopPropagation();
              openMap(addressText);
            },
            icon: FiMapPin,
            label: t("parentPortal.directory.open_map_short", { defaultValue: "Map" }),
          }
        : null,
      canAdd
        ? {
            key: "add",
            as: "button",
            onClick: (event) => {
              event.stopPropagation();
              downloadVCard(card);
            },
            icon: FiUserPlus,
            label: t("parentPortal.directory.add_contact", { defaultValue: "Add" }),
          }
        : null,
    ].filter(Boolean);

    const typeLabel = isChild
      ? t("parentPortal.directory.result_child", { defaultValue: "Child" })
      : isTeacher
        ? t("parentPortal.directory.result_teacher", { defaultValue: "Teacher" })
        : t("parentPortal.directory.result_parent", { defaultValue: "Parent" });
    const subtitle = (isChild || isTeacher) && hasClassroom ? `${typeLabel} • ${card.classroomName}` : typeLabel;

    const ContactButton = ({ action, compact = false }) => {
      const Icon = action.icon;
      const Component = action.as || "button";
      const props = Component === "a" ? { href: action.href } : { type: "button", onClick: action.onClick };
      return (
        <Component
          {...props}
          className={compact ? "pp-directory-icon-action" : "pp-directory-action"}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick?.(event);
          }}
          aria-label={action.label}
        >
          <Icon aria-hidden="true" />
          {!compact && <span>{action.label}</span>}
        </Component>
      );
    };

    const getParentActionButtons = (parent) => {
      const parentAddressText = getAddressText(parent);
      const parentAvailableActions = Array.isArray(parent.availableActions) ? parent.availableActions : null;
      const parentActionAllowed = (action, fallback) =>
        parentAvailableActions ? parentAvailableActions.includes(action) : fallback;
      const parentHasEmail = !!parent.email;
      const parentHasPhone = !!parent.phoneMobile;
      const parentHasAddress = !!parentAddressText;
      const parentCanAdd = parentActionAllowed(
        "ADD",
        parentHasEmail || parentHasPhone || parentHasAddress || !!parent.displayName
      );

      return [
        parentActionAllowed("MAIL", parentHasEmail)
          ? {
              key: "mail",
              as: "a",
              href: `mailto:${parent.email}`,
              icon: FiMail,
              label: t("parentPortal.directory.email_parent", { defaultValue: "Mail" }),
            }
          : null,
        parentActionAllowed("CALL", parentHasPhone)
          ? {
              key: "call",
              as: "a",
              href: `tel:${parent.phoneMobile.replace(/\s+/g, "")}`,
              icon: FiPhone,
              label: t("parentPortal.directory.call_parent", { defaultValue: "Call" }),
            }
          : null,
        parentActionAllowed("MAP", parentHasAddress)
          ? {
              key: "map",
              as: "button",
              onClick: (event) => {
                event.stopPropagation();
                openMap(parentAddressText);
              },
              icon: FiMapPin,
              label: t("parentPortal.directory.open_map_short", { defaultValue: "Map" }),
            }
          : null,
        parentCanAdd
          ? {
              key: "add",
              as: "button",
              onClick: (event) => {
                event.stopPropagation();
                downloadVCard(parent, { contextChildName: displayName });
              },
              icon: FiUserPlus,
              label: t("parentPortal.directory.add_contact", { defaultValue: "Add" }),
            }
          : null,
      ].filter(Boolean);
    };

    return (
      <li key={contactKey} className={`pp-directory-card ${isExpanded ? "is-expanded" : ""}`}>
        <button
          type="button"
          className="pp-directory-card-head"
          onClick={() => setExpandedContactKey((current) => (current === contactKey ? null : contactKey))}
          aria-expanded={isExpanded}
        >
          <span className="pp-directory-avatar">{getInitials(displayName)}</span>
          <span className="pp-directory-card-title-wrap">
            <span className="pp-directory-card-title">
              {displayName}
              {isTeacher && (
                <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-sky-50 px-2 py-0.5 align-middle text-[11px] font-bold text-sky-700 ring-1 ring-sky-100 dark:bg-sky-500/15 dark:text-sky-200 dark:ring-sky-500/25" data-label="teacher">
                {t("parentPortal.directory.teacher_label", { defaultValue: "Teacher" })}
              </span>
              )}
            </span>
            <span className="pp-directory-card-subtitle">{subtitle}</span>
            {!isExpanded && actionButtons.length > 0 && (
              <span className="pp-directory-inline-actions" aria-hidden="true">
                {actionButtons.map((action) => {
                  const Icon = action.icon;
                  return <Icon key={action.key} />;
                })}
              </span>
            )}
          </span>
          <FiChevronDown className="pp-directory-card-chevron" aria-hidden="true" />
        </button>

        {isExpanded && (
          <div className="pp-directory-expanded">
            {hasDetails && (
              <div className="pp-directory-details">
                {hasEmail && (
                  <div className="pp-directory-detail-row">
                    <FiMail aria-hidden="true" />
                    <span>{card.email}</span>
                  </div>
                )}
                {hasPhone && (
                  <div className="pp-directory-detail-row">
                    <FiPhone aria-hidden="true" />
                    <span>{card.phoneMobile}</span>
                  </div>
                )}
                {hasAddress && (
                  <div className="pp-directory-detail-row">
                    <FiMapPin aria-hidden="true" />
                    <span>{addressText}</span>
                  </div>
                )}
                {(isChild || isTeacher) && hasClassroom && (
                  <div className="pp-directory-detail-row">
                    <FiBookOpen aria-hidden="true" />
                    <span>{card.classroomName}</span>
                  </div>
                )}
                {isChild && hasParents && !hasParentContacts && (
                  <div className="pp-directory-detail-row">
                    <FiUsers aria-hidden="true" />
                    <span>{card.parentNames.join(", ")}</span>
                  </div>
                )}
              </div>
            )}


            {isChild && hasParentContacts && (
              <div className="pp-directory-parent-contact-list">
                <div className="pp-directory-parent-contact-heading">
                  {t("parentPortal.directory.parents", { defaultValue: "Parents" })}
                </div>
                {parentContacts.map((parent, parentIndex) => {
                  const parentActions = getParentActionButtons(parent);
                  const parentDisplayName = parent.displayName || t("common.unknown", { defaultValue: "Unknown" });
                  return (
                    <div
                      key={parent.parentId || parent.id || `${parentDisplayName}-${parentIndex}`}
                      className="pp-directory-parent-contact-row"
                    >
                      <div className="pp-directory-parent-contact-summary">
                        <span className="pp-directory-parent-mini-avatar">{getInitials(parentDisplayName)}</span>
                        <span className="pp-directory-parent-contact-copy">
                          <span className="pp-directory-parent-contact-name">{parentDisplayName}</span>
                          {parent.isNameOnly && parentActions.length === 0 && (
                            <span className="pp-directory-parent-contact-muted">
                              {t("parentPortal.directory.no_contact_details", {
                                defaultValue: "No contact details shared",
                              })}
                            </span>
                          )}
                        </span>
                      </div>

                      {parentActions.length > 0 ? (
                        <div className="pp-directory-parent-contact-actions">
                          {parentActions.map((action) => (
                            <ContactButton key={action.key} action={action} />
                          ))}
                        </div>
                      ) : (
                        <div className="pp-directory-parent-contact-empty">
                          {t("parentPortal.directory.no_contact_details", {
                            defaultValue: "No contact details shared",
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {actionButtons.length > 0 && (
              <div className="pp-directory-actions">
                {actionButtons.map((action) => (
                  <ContactButton key={action.key} action={action} />
                ))}
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="pp-directory-page pp-directory-app-view text-left text-slate-950 dark:text-slate-50">
      {dirSettingsOpen ? (
        renderSettings()
      ) : (
        <>
          <div className="pp-directory-hero">
            <div className="pp-directory-title-row">
              <div>
                <h3>{directoryTitle}</h3>
                <p>
                  {t("parentPortal.directory.subtitle", {
                    defaultValue: "Search parents and children within the school.",
                  })}
                </p>
              </div>

              <button
                type="button"
                onClick={openDirSettings}
                className="pp-directory-settings-button"
                aria-label={t("parentPortal.directory.edit_sharing", { defaultValue: "Settings" })}
              >
                <FiSettings aria-hidden="true" />
              </button>
            </div>
          </div>

          {!canViewDirectory ? (
            <div className="pp-directory-empty-card">
              <div className="pp-directory-empty-icon">
                <FiBookOpen aria-hidden="true" />
              </div>
              <h4>
                {t("parentPortal.directory.not_listed_title", {
                  defaultValue: "You are not listed in the directory yet.",
                })}
              </h4>
              <p>
                {t("parentPortal.directory.require_publish", {
                  defaultValue: "When you share your details, you can also search other parents.",
                })}
              </p>
              <button type="button" onClick={openDirSettings} className="pp-directory-primary-button">
                {t("parentPortal.directory.set_profile", { defaultValue: "Share details" })}
              </button>
            </div>
          ) : (
            <>
              <section className="pp-directory-search-card is-sticky" aria-label={t("parentPortal.directory.search", { defaultValue: "Search directory" })}>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitDirectorySearch();
                  }}
                >
                  <div className="pp-directory-search-field">
                    <FiSearch aria-hidden="true" />
                    <input
                      ref={dirInputRef}
                      type="text"
                      placeholder={genericSearchPlaceholder}
                      value={dirQ}
                      onChange={(event) => setDirQ(event.target.value)}
                      disabled={dirIsLoading}
                    />
                    {dirQ && !dirIsLoading && (
                      <button
                        type="button"
                        onClick={() => {
                          setDirQ("");
                          setUnifiedData(null);
                          setDirData?.(null);
                          setUnifiedError(null);
                          setExpandedContactKey(null);
                          dirInputRef.current?.focus();
                        }}
                        aria-label={t("common.clear", { defaultValue: "Clear" })}
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </form>
              </section>

              <section className="pp-directory-results">
                <div className="pp-directory-results-head">
                  <h4>
                    {hasResults
                      ? t("parentPortal.directory.results_heading_unified", { defaultValue: "Found results" })
                      : t("parentPortal.directory.search_heading", { defaultValue: "Search" })}
                  </h4>
                  {hasResults && <span>{totalElements}</span>}
                </div>

                {dirIsLoading && <div className="pp-directory-message-card">{t("common.loading", { defaultValue: "Loading..." })}</div>}

                {!dirIsLoading && unifiedError && <div className="pp-directory-message-card is-error">{unifiedError}</div>}

                {!dirIsLoading && unifiedData == null && (
                  <div className="pp-directory-hint-card">
                    {t("parentPortal.directory.search_hint", {
                      defaultValue: "Search a parent or child to view the directory.",
                    })}
                  </div>
                )}

                {!dirIsLoading && unifiedData && !hasResults && (
                  <div className="pp-directory-message-card">
                    {t("parentPortal.directory.no_results", { defaultValue: "No results found." })}
                  </div>
                )}

                {!dirIsLoading && hasResults && <ul className="pp-directory-list">{content.map(renderContactCard)}</ul>}
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DirectoryTab;
